import React, { useState, useEffect } from "react";
import { Clock, Search, RefreshCw, CheckCircle2, XCircle, PlayCircle, Settings, CalendarDays, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { Pagination } from "./Pagination";

interface ScheduledTask {
    id: number;
    name: string;
    path: string;
    action: string;
    state: string;
    enabled: boolean;
    hidden: boolean;
    last_run_time: number | string | null;
    next_run_time: number | string | null;
    last_run_message: string;
}

interface AssetScheduledTasksProps {
    assetId: string | number;
    client_id: string | number;
}

export default function AssetScheduledTasks({ assetId, client_id }: AssetScheduledTasksProps) {
    const [tasks, setTasks] = useState<ScheduledTask[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Refresh Key (used to force useEffect to re-run when clicking refresh)
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId,
                client_id: client_id
            };

            if (searchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${searchTerm}%` } },
                    { path: { $ilike: `%${searchTerm}%` } },
                    { action: { $ilike: `%${searchTerm}%` } }
                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_scheduled_tasks", { params });

            if (response.data.status) {
                setTasks(response.data.data || []);
                setTotalRecords(response.data.total || 0);
            } else {
                showError(response.data.message || "Failed to load scheduled tasks");
                setTasks([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load scheduled tasks");
            setTasks([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchTasks();
        }
        // Added refreshKey to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchTerm, assetId, sortConfig, refreshKey]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey }: { label: string; sortKey: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider cursor-pointer group select-none"
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };


    const renderStateBadge = (state: string) => {
        const lower = state?.toLowerCase() || '';
        if (lower.includes('ready')) {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    <CheckCircle2 size={10} /> Ready
                </span>
            );
        }
        if (lower.includes('running')) {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20 whitespace-nowrap">
                    <PlayCircle size={10} className="animate-pulse" /> Running
                </span>
            );
        }
        if (lower.includes('disabled')) {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-500/10 text-gray-200 border border-gray-500/20 whitespace-nowrap">
                    <XCircle size={10} /> Disabled
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-600/30 text-gray-300 border border-gray-600 whitespace-nowrap">
                {state || 'Unknown'}
            </span>
        );
    };

    return (
        <div className="flex flex-col h-full text-gray-200 relative p-2 pb-6">
            {/* Header & Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Clock className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-white leading-tight">Scheduled Tasks</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} tasks detected
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            // Reset state directly (fetchTasks() is no longer called here)
                            setCurrentPage(1);
                            setSearchInput("");
                            setSearchTerm("");
                            // Trigger re-render explicitly if values didn't actually change
                            setRefreshKey(prev => prev + 1);
                        }}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search tasks..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </form>

                </div>
            </div>

            {/* Table Area */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative min-h-[400px]">
                <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full table-fixed text-left min-w-[900px]">
                        <colgroup>
                            <col className="w-[20%]" />
                            <col className="w-[10%]" />
                            <col className="w-[25%]" />
                            <col className="w-[15%]" />
                            <col className="w-[15%]" />
                            <col className="w-[15%]" />
                        </colgroup>
                        <thead className="bg-gray-800 sticky top-0 z-10">
                            <tr className="border-b border-gray-700">
                                <SortableHeader label="Task Name & Path" sortKey="name" />
                                <SortableHeader label="State" sortKey="state" />
                                <SortableHeader label="Action / Command" sortKey="action" />
                                <SortableHeader label="Last Run Time" sortKey="lastruntime" />
                                <SortableHeader label="Next Run Time" sortKey="nextruntime" />
                                <SortableHeader label="Last Result" sortKey="lastrunmessage" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : tasks.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-50 h-full">
                                            <Clock size={32} className="text-gray-200 mb-2" />
                                            <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">No scheduled tasks found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                tasks.map((task) => {
                                    const isSuccess = task.last_run_message?.toLowerCase().includes('success');

                                    return (
                                        <tr key={task.id} className="hover:bg-gray-800/40 transition-colors duration-100">
                                            <td className="px-3 py-2">
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[11px] font-bold text-white truncate" title={task.name}>
                                                        {task.name}
                                                    </span>
                                                    <span className="text-[9px] text-gray-200 font-mono truncate mt-0.5" title={task.path}>
                                                        {task.path || "\\"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                {renderStateBadge(task.state)}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Settings size={12} className="text-gray-200 shrink-0" />
                                                    <span className="text-[10px] text-gray-300 font-mono truncate" title={task.action}>
                                                        {task.action || "-"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <CalendarDays size={10} className="text-gray-200 shrink-0" />
                                                    <span className="text-[10px] text-gray-300">
                                                        {task.last_run_time || "Never"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={10} className="text-blue-400 shrink-0" />
                                                    <span className="text-[10px] text-gray-300">
                                                        {task.next_run_time || "Never"}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span
                                                    className={`text-[10px] truncate block ${isSuccess ? 'text-emerald-400' : 'text-gray-200'
                                                        }`}
                                                    title={task.last_run_message}
                                                >
                                                    {task.last_run_message || "Unknown"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(totalRecords / limit))}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(val: number) => {
                        setLimit(val);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
}